'use client'

import { useMemo, type ComponentProps, type ReactNode } from 'react'

import {
  Link,
  useLocation,
  useNavigate,
  useRouter as useTanstackRouter
} from '@tanstack/react-router'

type AppLinkProps = Omit<ComponentProps<typeof Link>, 'to'> & {
  href: string
  children?: ReactNode
}

export function AppLink({ href, children, ...props }: AppLinkProps) {
  return (
    <Link to={href} {...props}>
      {children}
    </Link>
  )
}

export function useAppRouter() {
  const navigate = useNavigate()
  const router = useTanstackRouter()

  return {
    push: (path: string) => navigate({ to: path }),
    replace: (path: string) => navigate({ to: path, replace: true }),
    back: () => window.history.back(),
    prefetch: (path: string) => {
      void router.preloadRoute({ to: path }).catch(() => undefined)
    }
  }
}

export function usePathname() {
  const { pathname } = useLocation()
  return pathname
}

export function useSearchParams() {
  return useAppSearchParams()
}

export function useAppSearchParams() {
  const location = useLocation()

  return useMemo(() => {
    const params = new URLSearchParams(location.searchStr ?? '')

    return {
      get: (key: string) => {
        const fromSearch = (location.search as Record<string, unknown>)?.[key]
        if (fromSearch != null) return String(fromSearch)
        return params.get(key)
      }
    }
  }, [location.search, location.searchStr])
}
